import React from 'react';
import {
  Box,
  type SxProps,
  TableCell,
  TableRow,
  type Theme,
  Tooltip,
  Typography,
} from '@mui/material';
import { taskTableStyles } from '../taskUi.tsx';
import { tasksPageText } from '../constants.ts';
import {
  getTaskGuaranteesApplicationStatusConfig,
  taskStatusConfig,
  taskUrgencyConfig,
} from '../taskConfig.ts';
import { tasksPageStyles } from '../styles.ts';
import { formatTaskDate, getTaskDeadlineUrgency, getTaskDisplayValue } from '../utils.ts';
import TaskActionsMenu from './TaskActionsMenu.tsx';
import { TaskSlaIndicator } from './TaskSlaIndicator/TaskSlaIndicator.tsx';
import { TaskStatusChipWithTooltip as StatusChipWithTooltip } from './TaskStatusChipWithTooltip.tsx';
import type { ITaskTableRowProps } from './types.ts';

const TaskTableRow: React.FC<ITaskTableRowProps> = ({
  actionPath,
  activeGuaranteesActionId,
  canAssignGuaranteesTask,
  onGuaranteesRefuseTask,
  onGuaranteesSlaDeadlineReached,
  onGuaranteesAssignTask,
  onGuaranteesTakeInWork,
  row,
}) => {
  const rowActionPath = row.actionPath ?? actionPath;
  const guaranteeStatusConfig = getTaskGuaranteesApplicationStatusConfig(row.guaranteeStatus);
  const status = taskStatusConfig[row.status];
  const urgencyKey = getTaskDeadlineUrgency(row.deadline);
  const urgency = taskUrgencyConfig[urgencyKey];

  if (row.guaranteeStatus) {
    return (
      <TableRow
        hover
        sx={
          [
            tasksPageStyles.tableRow,
            tasksPageStyles.guaranteeSlaRow(row.slaOverdue === true),
          ] as SxProps<Theme>
        }
      >
        <TableCell sx={(theme) => tasksPageStyles.tableBodyCell(theme, 'document')}>
          <Typography
            variant="body2"
            sx={
              [
                taskTableStyles.tablePrimaryText,
                taskTableStyles.tablePrimaryTextMultiLine,
              ] as SxProps<Theme>
            }
          >
            {getTaskDisplayValue(row.counterparty)}
          </Typography>
          {row.isUrgent === true && (
            <Box component="span" sx={tasksPageStyles.urgentTaskBadge}>
              {tasksPageText.urgentTaskLabel}
            </Box>
          )}
          <Typography variant="caption" sx={tasksPageStyles.guaranteeCounterpartyMeta}>
            {getTaskDisplayValue(row.counterpartyCode)}
          </Typography>
        </TableCell>
        <TableCell align="center" sx={(theme) => tasksPageStyles.tableBodyCell(theme, 'createdAt')}>
          <Typography variant="body2" sx={taskTableStyles.tableDateText}>
            {formatTaskDate(row.date)}
          </Typography>
        </TableCell>
        <TableCell align="center" sx={(theme) => tasksPageStyles.tableBodyCell(theme, 'sla')}>
          {row.slaDeadline ? (
            <TaskSlaIndicator
              deadline={row.slaDeadline}
              overdue={row.slaOverdue}
              onDeadlineReached={() => onGuaranteesSlaDeadlineReached?.(row)}
            />
          ) : (
            <Typography variant="body2" sx={tasksPageStyles.slaCellPlaceholder}>
              {tasksPageText.emptyValue}
            </Typography>
          )}
        </TableCell>
        <TableCell align="center" sx={(theme) => tasksPageStyles.tableBodyCell(theme, 'status')}>
          <StatusChipWithTooltip
            label={guaranteeStatusConfig?.label ?? getTaskDisplayValue(row.guaranteeStatus)}
            color={guaranteeStatusConfig?.color ?? 'default'}
            description={
              row.guaranteeStatusTooltipDescription ??
              guaranteeStatusConfig?.description ??
              getTaskDisplayValue(row.guaranteeStatus)
            }
            chipSx={tasksPageStyles.guaranteeStatusChip}
          />
        </TableCell>
        <TableCell align="center" sx={(theme) => tasksPageStyles.tableBodyCell(theme, 'executor')}>
          <Typography variant="body2" sx={taskTableStyles.tableDateText}>
            {getTaskDisplayValue(row.assigneeEmail) || tasksPageText.emptyValue}
          </Typography>
        </TableCell>
        <TableCell align="center" sx={(theme) => tasksPageStyles.tableBodyCell(theme, 'actions')}>
          <TaskActionsMenu
            actionPath={rowActionPath}
            activeGuaranteesActionId={activeGuaranteesActionId}
            canAssignGuaranteesTask={canAssignGuaranteesTask}
            id={row.id}
            onGuaranteesAssignTask={onGuaranteesAssignTask}
            onGuaranteesRefuseTask={onGuaranteesRefuseTask}
            onGuaranteesTakeInWork={onGuaranteesTakeInWork}
            row={row}
          />
        </TableCell>
      </TableRow>
    );
  }

  return (
    <TableRow
      hover
      sx={[tasksPageStyles.tableRow, tasksPageStyles.taskRow(urgencyKey)] as SxProps<Theme>}
    >
      <TableCell sx={(theme) => tasksPageStyles.tableBodyCell(theme, 'name')}>
        <Typography
          variant="body2"
          sx={
            [
              taskTableStyles.tablePrimaryText,
              { fontWeight: 700, color: 'primary.main' },
            ] as SxProps<Theme>
          }
        >
          {row.reference}
        </Typography>
      </TableCell>
      <TableCell sx={(theme) => tasksPageStyles.tableBodyCell(theme, 'document')}>
        <Typography
          variant="body2"
          sx={
            [
              taskTableStyles.tablePrimaryText,
              taskTableStyles.tablePrimaryTextMultiLine,
            ] as SxProps<Theme>
          }
        >
          {row.subject}
        </Typography>
      </TableCell>
      <TableCell sx={(theme) => tasksPageStyles.tableBodyCell(theme, 'document')}>
        <Typography
          variant="body2"
          sx={
            [
              taskTableStyles.tablePrimaryText,
              taskTableStyles.tablePrimaryTextMultiLine,
            ] as SxProps<Theme>
          }
        >
          {row.task}
        </Typography>
      </TableCell>
      <TableCell align="center" sx={(theme) => tasksPageStyles.tableBodyCell(theme, 'deadline')}>
        <Box sx={tasksPageStyles.deadlineWrap}>
          <Typography variant="body2" sx={taskTableStyles.tableDateText}>
            {row.deadline}
          </Typography>
          <Tooltip
            title={urgency.description}
            placement="bottom-start"
            slotProps={{
              tooltip: { sx: taskTableStyles.statusTooltip },
            }}
          >
            <Box
              component="span"
              tabIndex={0}
              aria-label={urgency.description}
              sx={tasksPageStyles.urgencyIndicator(urgencyKey)}
            >
              <Box component="span" sx={tasksPageStyles.urgencyDot(urgencyKey)} />
              <Typography component="span" sx={tasksPageStyles.urgencyLabel}>
                {urgency.label}
              </Typography>
            </Box>
          </Tooltip>
        </Box>
      </TableCell>
      <TableCell align="center" sx={(theme) => tasksPageStyles.tableBodyCell(theme, 'status')}>
        <StatusChipWithTooltip
          label={status.label}
          color={status.color}
          description={status.description}
          chipSx={tasksPageStyles.guaranteeStatusChip}
        />
      </TableCell>
      <TableCell align="center" sx={(theme) => tasksPageStyles.tableBodyCell(theme, 'actions')}>
        <TaskActionsMenu id={row.id} actionPath={rowActionPath} row={row} />
      </TableCell>
    </TableRow>
  );
};

export default TaskTableRow;
